import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import type { IconType } from 'react-icons';
import {
    MdOutlineDashboard,
    MdOutlineFolder,
    MdOutlinePeople,
    MdOutlineGroup,
    MdOutlineAccountBalanceWallet,
    MdOutlineSettings,
    MdOutlineSearch,
    MdOutlineNotifications,
    MdOutlineMenu,
    MdClose,
    MdOutlineLogout,
} from 'react-icons/md';
import { FormSelect } from '@podus/ui';
import { useAuth } from '@/context/AuthContext';
import { YearProvider, useYear } from '@/context/YearContext';

export interface NavItem {
    label: string;
    icon: IconType;
    to: string;
}

export const ADMIN_NAV: NavItem[] = [
    { label: 'Dashboard', icon: MdOutlineDashboard, to: '/dashboard' },
    { label: 'Case Management', icon: MdOutlineFolder, to: '/dashboard/cases' },
    { label: 'Internal Users', icon: MdOutlineGroup, to: '/dashboard/users' },
    { label: 'Financials', icon: MdOutlineAccountBalanceWallet, to: '/dashboard/financials' },
    { label: 'Settings', icon: MdOutlineSettings, to: '/dashboard/settings' },
];

export const CASE_MANAGER_NAV: NavItem[] = [
    { label: 'Dashboard', icon: MdOutlineDashboard, to: '/case-manager' },
    { label: 'Case Management', icon: MdOutlineFolder, to: '/case-manager/cases' },
    { label: 'Settings', icon: MdOutlineSettings, to: '/case-manager/settings' },
];

export const MEDIATOR_NAV: NavItem[] = [
    { label: 'Dashboard', icon: MdOutlineDashboard, to: '/mediator' },
    { label: 'My Cases', icon: MdOutlineFolder, to: '/mediator/cases' },
    { label: 'Settings', icon: MdOutlineSettings, to: '/mediator/settings' },
];

interface DashboardLayoutProps {
    navItems?: NavItem[];
    basePath?: string;
}

export default function DashboardLayout(props: DashboardLayoutProps) {
    return (
        <YearProvider>
            <DashboardLayoutInner {...props} />
        </YearProvider>
    );
}

function DashboardLayoutInner({ navItems = ADMIN_NAV, basePath = '/dashboard' }: DashboardLayoutProps) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const { year, setYear, yearOptions } = useYear();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const initial = user?.name?.charAt(0).toUpperCase() ?? 'A';

    return (
        <div className="flex h-dvh bg-white">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* ── Sidebar ── */}
            <aside
                className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-[#E0E0E0] bg-white transition-transform lg:static lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex h-16 items-center justify-center gap-2 border-b border-[#E0E0E0] px-5">
                    <img src="/logo/multi-door.svg" alt="OYSMDC" className="h-4.5 w-auto" />
                </div>

                <nav className="flex-1 overflow-y-auto px-3 py-4 mt-2">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === basePath}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-lg px-3 py-2.5 mb-3 text-[15px] transition-colors leading-6 ${
                                    isActive ? 'font-extrabold text-secondary' : 'text-[#4E4D4D] hover:bg-[#f5f5f5]'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon size={20} className={isActive ? 'text-primary' : ''} />
                                    {item.label}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </aside>

            {/* ── Main area ── */}
            <div className="flex flex-1 flex-col overflow-hidden">
                {/* Header */}
                <header className="flex h-16 shrink-0 items-center gap-4 border-b border-[#E0E0E0] px-4 lg:px-8">
                    <button type="button" title="Open menu" onClick={() => setSidebarOpen(true)} className="lg:hidden">
                        <MdOutlineMenu size={24} className="text-secondary" />
                    </button>

                    <div className="w-28">
                        <FormSelect
                            value={String(year)}
                            onChange={(v) => setYear(Number(v))}
                            options={yearOptions}
                        />
                    </div>

                    <div className="flex-1" />

                    {/* <div className="hidden items-center gap-2 rounded-lg bg-[#f5f5f5] px-4 py-2 sm:flex">
                        <MdOutlineSearch size={20} className="text-[#999]" />
                        <input
                            type="text"
                            placeholder="Search"
                            className="w-48 bg-transparent text-[15px] text-secondary outline-none placeholder:text-[#999] lg:w-64"
                        />
                    </div> */}

                    {/* <button
                        type="button"
                        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdOutlineNotifications size={20} />
                    </button> */}
                    <button
                        type="button"
                        title="Settings"
                        onClick={() => navigate(`${basePath}/settings`)}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-bold text-white transition-opacity hover:opacity-80"
                    >
                        {initial}
                    </button>
                    <button
                        type="button"
                        title="Logout"
                        onClick={handleLogout}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdOutlineLogout size={20} />
                    </button>
                </header>

                <main className="flex-1 overflow-y-auto">
                    <Outlet />
                </main>
            </div>

            {/* Mobile close button */}
            {sidebarOpen && (
                <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="fixed left-64 top-4 z-50 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md lg:hidden"
                >
                    <MdClose size={18} />
                </button>
            )}
        </div>
    );
}
